import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import {
    ArrowSideScrollContainer,
    ArrowSideScrollContainerProps,
} from '@lumapps/lumx-layouts/components/ArrowSideScrollContainer';
import { useTranslate, GLOBAL } from '@lumapps/translations';
import { isLastItem } from '@lumapps/utils/array/isLastItem';

import { BreadCrumbLevel } from '../../types';
import { BreadcrumbItem } from '../BreadcrumbItem';

import './index.scss';

export interface BreadcrumbProps {
    /** Custom class name to apply to the breadcrumb's root element. */
    className?: string;
    /**
     * Whether the breadcrumb is displayed with minimal spacing, suitable for inline placement.
     * @default false
     */
    isInline?: boolean;
    /** An array of `BreadCrumbLevel` objects to be displayed. */
    levels: BreadCrumbLevel[];
    /** Vertical padding (only works when not in inline mode) */
    paddingVertical?: ArrowSideScrollContainerProps['paddingVertical'];
    /** Horizontal padding (only works when not in inline mode) */
    paddingHorizontal?: ArrowSideScrollContainerProps['paddingHorizontal'];
}

const CLASSNAME = 'lumx-breadcrumb';

/**
 * Navigation breadcrumb
 * @param {BreadcrumbProps}
 * @returns Breadcrumb
 */
export const Breadcrumb = ({
    className,
    isInline = false,
    levels,
    paddingHorizontal,
    paddingVertical,
}: BreadcrumbProps) => {
    const { translateKey } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);

    if (levels.length === 0) {
        return null;
    }

    const breadcrumb = (
        <nav className={block([className])} aria-label={translateKey(GLOBAL.BREADCRUMB)}>
            <ol className={element('list')}>
                {levels.map((level, index) => (
                    <BreadcrumbItem
                        key={level.ariaLabel}
                        isInline={isInline}
                        item={level}
                        isLast={isLastItem(levels, index)}
                    />
                ))}
            </ol>
        </nav>
    );

    if (!isInline) {
        return (
            <ArrowSideScrollContainer paddingVertical={paddingVertical} paddingHorizontal={paddingHorizontal}>
                {breadcrumb}
            </ArrowSideScrollContainer>
        );
    }

    return breadcrumb;
};
